<template>
  <v-container
    fluid
    class="login-page pa-0"
    :class="{ 'dark-mode': isDark }"
    :dir="isArabic ? 'rtl' : 'ltr'"
  >
    <header class="login-header">
      <div class="header-brand">
        <div class="logo-box-header">
          <v-icon size="34" color="#173f6b"> mdi-hospital-building </v-icon>
        </div>

        <div class="header-text">
          <h1>
            {{ $t("login.title") }}
          </h1>

          <p>
            {{ $t("login.subtitle") }}
          </p>
        </div>
      </div>

      <div class="header-actions">
        <v-btn class="language-btn" variant="outlined" @click="toggleLanguage">
          <v-icon size="19"> mdi-translate </v-icon>

          {{ isArabic ? "العربية" : "English" }}
        </v-btn>
        <!-- <div class="header-badge">
          {{ $t("login.phase") }}
        </div> -->
      </div>
    </header>

    <main class="login-content">
      <section class="hero-panel">
        <div class="hero-watermark">
          <v-icon size="260"> mdi-hospital-building </v-icon>
        </div>

        <div class="hero-content">
          <div class="hero-icon">
            <v-icon size="42"> mdi-hospital-building </v-icon>
          </div>

          <h2>
            {{ $t("login.heroTitle") }}
          </h2>

          <p>
            {{ $t("login.heroDescription") }}
          </p>

          <div class="features-grid">
            <div class="feature-card">
              <v-icon> mdi-domain </v-icon>

              <span>
                {{ $t("login.featureFacility") }}
              </span>
            </div>

            <div class="feature-card">
              <v-icon> mdi-chart-box-outline </v-icon>

              <span>
                {{ $t("login.featureIndicators") }}
              </span>
            </div>

            <div class="feature-card">
              <v-icon> mdi-file-chart-outline </v-icon>

              <span>
                {{ $t("login.featureReports") }}
              </span>
            </div>
          </div>
        </div>
      </section>

      <section class="hfas-login-form-panel">
        <div class="hfas-login-card">
          <div class="card-logo">
            <v-icon size="42"> mdi-clipboard-pulse-outline </v-icon>
          </div>

          <h3>
            {{ $t("login.signIn") }}
          </h3>

          <p>
            {{ $t("login.signInDescription") }}
          </p>

          <v-alert
            v-if="errorMessage"
            type="error"
            variant="tonal"
            density="comfortable"
            class="mb-4"
            closable
            @click:close="errorMessage = ''"
          >
            {{ errorMessage }}
          </v-alert>

          <form
            ref="loginForm"
            class="login-form"
            @submit.prevent="handleLogin"
          >
            <div class="field-label">
              {{
                isArabic
                  ? "اسم المستخدم أو البريد الإلكتروني"
                  : "Username or email"
              }}
            </div>

            <v-text-field
              v-model="login"
              :placeholder="
                isArabic
                  ? 'أدخل اسم المستخدم أو البريد الإلكتروني'
                  : 'Enter username or email'
              "
              prepend-inner-icon="mdi-account-outline"
              variant="outlined"
              density="comfortable"
              class="mb-3 custom-field"
              type="text"
              autocomplete="username"
              :rules="loginRules"
            />

            <div class="field-label">
              {{ $t("login.password") }}
            </div>

            <v-text-field
              v-model="password"
              :placeholder="$t('login.passwordPlaceholder')"
              prepend-inner-icon="mdi-lock-outline"
              :type="showPassword ? 'text' : 'password'"
              :append-inner-icon="
                showPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'
              "
              variant="outlined"
              density="comfortable"
              class="mb-2 custom-field"
              autocomplete="current-password"
              :rules="passwordRules"
              @click:append-inner="showPassword = !showPassword"
            />

            <div class="form-options">
              <v-checkbox
                v-model="remember"
                :label="$t('login.rememberMe')"
                density="compact"
                hide-details
              />

              <button type="button" class="forgot-btn" @click="forgotPassword">
                {{ $t("login.forgotPassword") }}
              </button>
            </div>

            <v-btn
              type="submit"
              block
              height="48"
              class="login-btn"
              :loading="loading"
              :disabled="loading"
            >
              <span>
                {{ $t("login.enterSystem") }}
              </span>

              <v-icon :class="isArabic ? 'mr-2' : 'ml-2'"> mdi-login </v-icon>
            </v-btn>
          </form>
        </div>
      </section>
    </main>
  </v-container>
</template>

<script setup>
import api from "@/plugins/api";
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";

const router = useRouter();
const { locale, t } = useI18n();

const loginForm = ref(null);
const login = ref("");
const password = ref("");
const showPassword = ref(false);
const remember = ref(false);
const loading = ref(false);
const errorMessage = ref("");
const isDark = ref(false);

const isArabic = computed(() => locale.value === "ar");

const loginRules = computed(() => [
  (value) =>
    !!String(value || "").trim() || t("login.validation.emailRequired"),
]);

const passwordRules = computed(() => [
  (value) => !!value || t("login.validation.passwordRequired"),

  (value) =>
    String(value || "").length >= 6 || t("login.validation.passwordMin"),
]);

const applyLanguage = () => {
  const currentLanguage = locale.value === "en" ? "en" : "ar";

  document.documentElement.lang = currentLanguage;
  document.documentElement.dir = currentLanguage === "ar" ? "rtl" : "ltr";

  document.body.dir = currentLanguage === "ar" ? "rtl" : "ltr";

  localStorage.setItem("lang", currentLanguage);
};

const toggleLanguage = () => {
  locale.value = locale.value === "ar" ? "en" : "ar";

  applyLanguage();
};

const clearOldAuthentication = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("user");
  localStorage.removeItem("role");
  localStorage.removeItem("permissions");
  localStorage.removeItem("isAuthenticated");
  localStorage.removeItem("token_expires_at");
};

const handleLogin = async () => {
  errorMessage.value = "";

  const currentLogin = String(login.value || "").trim();

  const currentPassword = String(password.value || "");

  if (!currentLogin) {
    errorMessage.value = t("login.validation.emailRequired");

    return;
  }

  if (!currentPassword) {
    errorMessage.value = t("login.validation.passwordRequired");

    return;
  }

  if (currentPassword.length < 6) {
    errorMessage.value = t("login.validation.passwordMin");

    return;
  }

  loading.value = true;

  try {
    clearOldAuthentication();

    const response = await api.post("/login", {
      login: currentLogin,
      password: currentPassword,
      device_name: "vue-web",
    });

    const {
      token,
      user,
      role,
      permissions,
      expires_at: expiresAt,
    } = response.data;

    if (!token || !user) {
      throw new Error("Invalid login response");
    }

    const resolvedRole = role ?? user.role ?? null;

    const resolvedPermissions =
      permissions ?? user.role?.permissions ?? user.permissions ?? [];

    localStorage.setItem("token", token);
    localStorage.setItem("user", JSON.stringify(user));

    localStorage.setItem("role", JSON.stringify(resolvedRole));

    localStorage.setItem("permissions", JSON.stringify(resolvedPermissions));

    localStorage.setItem("isAuthenticated", "true");

    if (expiresAt) {
      localStorage.setItem("token_expires_at", expiresAt);
    }

    if (remember.value) {
      localStorage.setItem("remembered_login", currentLogin);
    } else {
      localStorage.removeItem("remembered_login");
      localStorage.removeItem("remembered_email");
    }

    loading.value = false;

    await router.replace("/home");
  } catch (error) {
    const validationErrors = error?.response?.data?.errors;

    const firstValidationError = validationErrors
      ? Object.values(validationErrors).flat()[0]
      : null;

    if (!error?.response && error?.message !== "Invalid login response") {
      errorMessage.value = isArabic.value
        ? "تعذر الاتصال بالخادم."
        : "Could not connect to the server.";
    } else {
      errorMessage.value =
        firstValidationError ||
        error?.response?.data?.message ||
        t("login.loginError");
    }

    loading.value = false;
  }
};

const forgotPassword = () => {
  router.push("/forgot-password");
};

onMounted(() => {
  const savedLanguage = localStorage.getItem("lang");

  if (savedLanguage === "ar" || savedLanguage === "en") {
    locale.value = savedLanguage;
  }

  const rememberedLogin =
    localStorage.getItem("remembered_login") ||
    localStorage.getItem("remembered_email");

  if (rememberedLogin) {
    login.value = rememberedLogin;
    remember.value = true;
  }

  isDark.value = localStorage.getItem("theme") === "dark";

  applyLanguage();
});
</script>
<style scoped>
.login-page {
  width: 100%;
  min-height: 100vh;
  margin: 0 !important;
  padding: 0 !important;
  overflow-x: hidden;

  background: linear-gradient(180deg, #eef3f8 0%, #f7f9fc 100%);

  color: #0f172a;
  font-family: "Cairo";
}

.login-page[dir="rtl"] {
  direction: rtl;
  text-align: right;
}

.login-page[dir="ltr"] {
  direction: ltr;
  text-align: left;
}

.login-page[dir="rtl"] .login-content {
  grid-template-areas: "hero login";
}

.login-page[dir="ltr"] .login-content {
  grid-template-areas: "login hero";
}

.hero-panel {
  grid-area: hero;
}

.hfas-login-form-panel {
  grid-area: login;
}

/* =========================
   رأس الصفحة
========================= */

.login-header {
  min-height: 76px;
  background: #173f6b;
  border-bottom: 4px solid #c49a3a;

  display: flex;
  align-items: center;
  justify-content: space-between;

  gap: 20px;
  padding: 12px 32px;

  color: white;

  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.14);
}

.header-brand {
  min-width: 0;

  display: flex;
  align-items: center;
  gap: 14px;
}

.logo-box-header {
  width: 58px;
  height: 48px;
  flex: 0 0 auto;

  border-radius: 12px;
  background: #ffffff;

  display: flex;
  align-items: center;
  justify-content: center;
}

.header-text {
  min-width: 0;
}

.header-brand h1 {
  margin: 0;

  color: #ffffff !important;
  font-size: 19px;
  font-weight: 900;
  line-height: 1.5;
}

.header-brand p {
  margin: 3px 0 0;

  color: #d9e6f3 !important;
  font-size: 12px;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 0 0 auto;
}

.header-badge {
  background: linear-gradient(135deg, #d5ad4a, #bd8f27);

  color: #111827;

  padding: 9px 22px;
  border-radius: 999px;

  font-size: 13px;
  font-weight: 900;
  white-space: nowrap;
}

.language-btn {
  min-width: 106px;
  height: 39px !important;

  color: #ffffff !important;
  border-color: rgba(255, 255, 255, 0.4) !important;

  border-radius: 10px !important;
  font-size: 13px !important;
  font-weight: 800 !important;
}

.language-btn:hover {
  background: rgba(255, 255, 255, 0.12) !important;
}

/* =========================
   محتوى الصفحة
========================= */

.login-content {
  width: 100%;
  min-height: calc(100vh - 76px);

  display: grid;
  grid-template-columns: minmax(390px, 0.95fr) minmax(0, 1.05fr);
  grid-template-rows: minmax(0, 1fr);

  gap: 28px;
  padding: 34px 48px;

  box-sizing: border-box;
  align-items: stretch;
}

/* =========================
   القسم التعريفي
========================= */

.hero-panel {
  position: relative;
  min-width: 0;
  min-height: 620px;
  overflow: hidden;

  border-radius: 30px;

  background: linear-gradient(135deg, #173f6b, #102d50);

  box-shadow: 0 20px 45px rgba(15, 23, 42, 0.16);
}

.hero-watermark {
  position: absolute;
  bottom: 35px;

  opacity: 0.06;
  color: white;
}

.login-page[dir="rtl"] .hero-watermark {
  left: 45px;
}

.login-page[dir="ltr"] .hero-watermark {
  right: 45px;
}

.hero-content {
  position: relative;
  z-index: 2;

  padding: 48px;
  color: white;
}

.hero-icon {
  width: 76px;
  height: 76px;

  margin-bottom: 24px;

  border-radius: 24px;
  background: rgba(196, 154, 58, 0.18);

  border: 1px solid rgba(196, 154, 58, 0.55);

  color: #d5ad4a;

  display: flex;
  align-items: center;
  justify-content: center;
}

.hero-content h2 {
  margin: 0 0 14px;

  color: #ffffff !important;

  font-size: 34px;
  font-weight: 900;
  line-height: 1.45;
}

.hero-content p {
  max-width: 620px;
  margin-bottom: 30px;

  color: #dbeafe !important;

  font-size: 16px;
  line-height: 2;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);

  gap: 15px;
}

.feature-card {
  min-height: 96px;

  border-radius: 20px;

  background: rgba(255, 255, 255, 0.09);

  border: 1px solid rgba(255, 255, 255, 0.14);

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  gap: 9px;

  color: #ffffff;
  font-weight: 900;

  transition: transform 0.2s ease, background-color 0.2s ease;
}

.feature-card:hover {
  transform: translateY(-3px);

  background: rgba(255, 255, 255, 0.14);
}

.feature-card .v-icon {
  color: #d5ad4a;
}

/* =========================
   نموذج تسجيل الدخول
========================= */

.hfas-login-form-panel {
  position: relative !important;
  z-index: 5 !important;

  display: flex !important;
  align-items: center;
  justify-content: center;

  width: 100%;
  min-width: 0;
  min-height: 620px;

  visibility: visible !important;
  opacity: 1 !important;
  overflow: visible !important;
}

.hfas-login-form-panel,
.hfas-login-form-panel * {
  box-sizing: border-box;
}

.hfas-login-form-panel[hidden],
.hfas-login-card[hidden] {
  display: flex !important;
}

.hfas-login-card {
  position: relative !important;
  z-index: 6 !important;

  display: block !important;
  width: min(100%, 430px) !important;
  max-width: 430px;
  height: auto !important;
  min-height: 470px;

  margin: 0 auto;
  padding: 34px;

  visibility: visible !important;
  opacity: 1 !important;
  overflow: visible !important;

  border-radius: 28px !important;
  background: #ffffff !important;
  border: 1px solid #dce7f2;

  box-shadow: 0 20px 45px rgba(15, 23, 42, 0.12) !important;
}

.card-logo {
  width: 74px;
  height: 74px;

  margin: 0 auto 18px;

  border-radius: 24px;

  background: linear-gradient(135deg, #eef5fb, #ffffff);

  border: 1px solid #dce7f2;
  color: #173f6b;

  display: flex;
  align-items: center;
  justify-content: center;
}

.hfas-login-card h3 {
  margin: 0;

  text-align: center;
  color: #173f6b !important;

  font-size: 26px;
  font-weight: 900;
}

.hfas-login-card > p {
  margin: 8px 0 24px;

  text-align: center;
  color: #64748b !important;

  font-size: 14px;
}

.field-label {
  margin-bottom: 7px;

  color: #173f6b;
  font-size: 14px;
  font-weight: 900;
}

.form-options {
  margin: 4px 0 18px;

  display: flex;
  align-items: center;
  justify-content: space-between;

  gap: 10px;
}

.forgot-btn {
  padding: 0;

  border: 0;
  background: transparent;

  color: #173f6b;
  font-size: 13px;
  font-weight: 800;

  cursor: pointer;
}

.forgot-btn:hover {
  text-decoration: underline;
}

.login-btn {
  background: linear-gradient(135deg, #173f6b, #102d50) !important;

  color: white !important;

  border-radius: 15px !important;

  font-size: 15px !important;
  font-weight: 900 !important;

  box-shadow: 0 12px 24px rgba(23, 63, 107, 0.22);
}

.login-form {
  display: block !important;
  width: 100%;
  height: auto !important;
  min-height: 1px;
  margin: 0;
  padding: 0;
  visibility: visible !important;
  opacity: 1 !important;
  overflow: visible !important;
}

/* =========================
   حقول Vuetify
========================= */

.hfas-login-card :deep(.v-field) {
  min-height: 48px !important;
  border-radius: 15px !important;
  background: #ffffff !important;
}

.hfas-login-card :deep(.v-field__input) {
  min-height: 48px !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  font-size: 14px;
}

.hfas-login-card :deep(.v-field__prepend-inner),
.hfas-login-card :deep(.v-field__append-inner) {
  padding-top: 12px !important;
}

.hfas-login-card :deep(input::placeholder) {
  color: #9aa6b2;
  opacity: 1;
}

.hfas-login-card :deep(.v-selection-control) {
  min-height: 32px !important;
}

.hfas-login-card :deep(.v-label) {
  color: #64748b;
  font-size: 13px;
}

/* =========================
   الوضع الليلي بدون :global
========================= */

.login-page.dark-mode {
  background: linear-gradient(180deg, #0f172a 0%, #111827 100%);
  color: #e2e8f0;
}

.login-page.dark-mode .hfas-login-card {
  background: #1e293b !important;
  border-color: #334155 !important;
}

.login-page.dark-mode .hfas-login-card h3,
.login-page.dark-mode .hfas-login-card .field-label {
  color: #f8fafc !important;
}

.login-page.dark-mode .hfas-login-card > p {
  color: #cbd5e1 !important;
}

.login-page.dark-mode .card-logo {
  background: #273449;
  border-color: #475569;
  color: #dbeafe;
}

.login-page.dark-mode .hfas-login-card :deep(.v-field) {
  background: #273449 !important;
}

.login-page.dark-mode .hfas-login-card :deep(.v-field__input) {
  color: #f8fafc !important;
}

.login-page.dark-mode .hfas-login-card :deep(input::placeholder) {
  color: #94a3b8 !important;
}

.login-page.dark-mode .hfas-login-card :deep(.v-label) {
  color: #cbd5e1 !important;
}

.login-page.dark-mode .forgot-btn {
  color: #dbeafe;
}

/* =========================
   الاستجابة للشاشات
========================= */

@media (max-width: 1100px) {
  .login-content,
  .login-page[dir="rtl"] .login-content,
  .login-page[dir="ltr"] .login-content {
    min-height: calc(100vh - 76px);
    grid-template-columns: 1fr;
    grid-template-rows: auto auto;
    grid-template-areas:
      "hero"
      "login";

    gap: 24px;
    padding: 28px 20px;
  }

  .hero-panel {
    min-height: auto;
  }

  .hero-content {
    min-height: 470px;
    padding: 36px 26px;
  }

  .hero-content h2 {
    font-size: 28px;
  }

  .hfas-login-form-panel {
    min-height: auto;
    padding: 10px 0 30px;
  }
}

@media (max-width: 850px) {
  .login-header {
    align-items: flex-start;

    padding: 16px 20px;
  }

  .header-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .header-badge {
    text-align: center;
  }
}

@media (max-width: 760px) {
  .login-header {
    flex-direction: column;
    align-items: center;

    gap: 14px;

    text-align: center;
  }

  .header-brand {
    flex-direction: column;
  }

  .header-brand h1 {
    font-size: 17px;
  }

  .header-actions {
    width: 100%;
    align-items: center;
  }

  .header-badge {
    font-size: 12px;
    padding: 9px 16px;
  }

  .features-grid {
    grid-template-columns: 1fr;
  }

  .hfas-login-card {
    padding: 28px 20px;
  }

  .form-options {
    flex-direction: column;
    align-items: flex-start;
  }

  .login-page[dir="ltr"] .form-options {
    align-items: flex-start;
  }
}
</style>
